import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, ClipboardList, Printer, Shirt } from "lucide-react";
import api from "@/lib/api";
import { AssignmentGroups } from "@/components/costume/AssignmentGroups";
import { EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fileUrl = (u) => (u && u.startsWith("/api") ? `${BACKEND}${u}` : u || "");
const FIT = {
  needs_fitting: { label: "Needs fitting", c: "bg-yellow-50 text-yellow-700" },
  assigned: { label: "Assigned from stock", c: "bg-teal text-white" },
  needs_order: { label: "Needs size ordered", c: "bg-red-100 text-red-700" },
};

export const CostumeRentals = ({ pid, production, routines, classes, canEdit, isDirector }) => {
  const [inventory, setInventory] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [toOrder, setToOrder] = useState([]);
  const [items, setItems] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({ inventory_id: "", target: "" });

  const load = () => {
    api.get("/costumes/inventory").then((r) => setInventory(r.data)).catch(() => {});
    api.get("/costumes/items").then((r) => setItems(r.data)).catch(() => {});
    api.get(`/costumes/productions/${pid}/assignments`).then((r) => setAssignments(r.data)).catch(() => {});
    api.get(`/costumes/productions/${pid}/to-order`).then((r) => setToOrder(r.data)).catch(() => {});
  };
  useEffect(() => { if (pid) load(); /* eslint-disable-next-line */ }, [pid]);

  const targets = useMemo(() => {
    const cls = classes.filter((c) => canEdit(c.id)).map((c) => ({ v: `class:${c.id}`, label: c.name }));
    const leads = isDirector ? routines.filter((r) => !r.class_id).map((r) => ({ v: `routine:${r.id}`, label: `Lead: ${r.title}` })) : [];
    return [...cls, ...leads];
  }, [classes, routines, canEdit, isDirector]);

  const assign = async () => {
    if (!form.inventory_id || !form.target) { toast.error("Pick a costume and a class/dance"); return; }
    const [type, id] = form.target.split(":");
    try {
      await api.post(`/costumes/productions/${pid}/assignments`, { inventory_id: form.inventory_id, class_id: type === "class" ? id : "", routine_id: type === "routine" ? id : "" });
      toast.success("Linked to class — now choose which dancers wear it"); setDlg(false); setForm({ inventory_id: "", target: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const removeAssign = async (a) => { try { await api.delete(`/costumes/assignments/${a.id}`); toast.success("Removed"); load(); } catch { toast.error("Error"); } };
  const saveFit = async (aid, did, patch) => { try { await api.put(`/costumes/assignments/${aid}/fittings/${did}`, patch); load(); } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); load(); } };

  const printLabels = () => {
    const esc = (s) => (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const labels = [];
    assignments.forEach((a) => a.roster.forEach((d) => {
      if (d.status === "assigned" && d.size) labels.push({ dancer: d.dancer_name, size: d.size, costume: `${a.inventory_name}${d.item_number ? ` #${d.item_number}` : ""}`, where: a.target_label });
    }));
    if (!labels.length) { toast.error("No dancers assigned from stock yet — assign sizes first."); return; }
    labels.sort((x, y) => x.dancer.localeCompare(y.dancer));
    const prod = production?.name || "Production";
    const cards = labels.map((l) => `<div class="label"><div class="prod">${esc(prod)}</div><div class="dancer">${esc(l.dancer)}</div><div class="size">Size <strong>${esc(l.size)}</strong></div><div class="costume">${esc(l.costume)}</div><div class="where">${esc(l.where)}</div></div>`).join("");
    const w = window.open("", "_blank");
    if (!w) { toast.error("Allow pop-ups to print labels"); return; }
    w.document.write(`<!doctype html><html><head><title>Costume Labels — ${esc(prod)}</title><meta charset="utf-8"><style>
      body{font-family:'Helvetica Neue',Arial,sans-serif;color:#111;margin:16px;}
      .grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}
      .label{border:1px dashed #999;border-radius:8px;padding:14px 16px;height:150px;display:flex;flex-direction:column;justify-content:center;text-align:center;break-inside:avoid}
      .prod{font-size:10px;letter-spacing:1px;text-transform:uppercase;color:#0d9488;font-weight:bold}
      .dancer{font-size:24px;font-weight:800;margin:4px 0}
      .size{font-size:18px;margin:2px 0}
      .size strong{color:#0d9488}
      .costume{font-size:14px;color:#333;margin-top:2px}
      .where{font-size:11px;color:#777}
      @media print{body{margin:8px}}
    </style></head><body><div class="grid">${cards}</div>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`);
    w.document.close();
  };

  const [avery, setAvery] = useState({ open: false, scope: "all", classKey: "", dancers: [], start: 1, template: "5161" });
  const averyRows = () => {
    let rows = [];
    assignments.forEach((a) => a.roster.forEach((d) => {
      if (d.status === "assigned" && d.size) rows.push({ name: d.dancer_name, size: d.size, costume: `${a.inventory_name}${d.item_number ? ` #${d.item_number}` : ""}`, where: a.target_label, key: a.class_id ? `class:${a.class_id}` : `routine:${a.routine_id}` });
    }));
    if (avery.scope === "class" && avery.classKey) rows = rows.filter((r) => r.key === avery.classKey);
    if (avery.scope === "dancer" && avery.dancers.length) rows = rows.filter((r) => avery.dancers.includes(r.name));
    rows.sort((x, y) => x.name.localeCompare(y.name));
    return rows;
  };
  const averyDancers = [...new Set(assignments.flatMap((a) => a.roster.filter((d) => d.status === "assigned" && d.size).map((d) => d.dancer_name)))].sort();
  const averyClasses = [...new Map(assignments.map((a) => [a.class_id ? `class:${a.class_id}` : `routine:${a.routine_id}`, a.target_label])).entries()];
  const downloadAvery = async () => {
    const rows = averyRows();
    if (!rows.length) { toast.error("No assigned-from-stock labels match — assign sizes first."); return; }
    try {
      const r = await api.post("/costumes/labels/avery", { labels: rows.map(({ name, costume, where, size }) => ({ name, costume, where, size })), start_position: Number(avery.start) || 1, template: avery.template }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = "costume_hanger_labels.pdf"; a.click(); URL.revokeObjectURL(url);
      toast.success(`${rows.length} label(s) exported`); setAvery((v) => ({ ...v, open: false }));
    } catch { toast.error("Could not build the PDF"); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-head font-bold text-lg flex items-center gap-2"><Shirt className="w-5 h-5 text-teal" />Rental Costume Fittings</h3>
        <div className="flex flex-wrap items-center gap-2">
          <Button data-testid="print-labels-button" onClick={printLabels} variant="outline" className="rounded-full"><Printer className="w-4 h-4 mr-2" />Print Labels</Button>
          <Button data-testid="avery-labels-button" onClick={() => setAvery((v) => ({ ...v, open: true, start: 1 }))} variant="outline" className="rounded-full"><Printer className="w-4 h-4 mr-2" />Avery hanger labels</Button>
          <Button data-testid="assign-costume-button" onClick={() => setDlg(true)} className="rounded-full bg-teal hover:bg-teal-dark"><Plus className="w-4 h-4 mr-2" />Assign costume</Button>
        </div>
      </div>

      <Dialog open={avery.open} onOpenChange={(o) => setAvery((v) => ({ ...v, open: o }))}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="avery-dialog">
          <DialogHeader><DialogTitle className="font-head">Avery 5161/8161 hanger labels</DialogTitle></DialogHeader>
          <div className="space-y-4 text-sm">
            <p className="text-xs text-muted-foreground">Letter sheet — the same info prints on both halves of each label so it reads upright from either side after folding at the midpoint. Exports a print-ready PDF at actual size (turn OFF “fit to page” when printing).</p>
            <div>
              <Label>Label stock (Avery template)</Label>
              <select data-testid="avery-template-select" className="w-full border rounded-md h-10 px-3 mt-1.5" value={avery.template} onChange={(e) => setAvery((v) => ({ ...v, template: e.target.value, start: 1 }))}>
                <option value="5161">5161 / 8161 — 1″ × 4″ (20 per sheet)</option>
                <option value="5160">5160 / 8160 — 1″ × 2⅝″ (30 per sheet)</option>
                <option value="5163">5163 / 8163 — 2″ × 4″ (10 per sheet)</option>
                <option value="5162">5162 — 1⅓″ × 4″ (14 per sheet)</option>
              </select>
            </div>
            <div>
              <Label>Who to print for</Label>
              <div className="flex gap-2 mt-1.5">
                {[["all", "This production"], ["class", "By class/dance"], ["dancer", "Selected dancers"]].map(([val, lbl]) => (
                  <button key={val} type="button" data-testid={`avery-scope-${val}`} onClick={() => setAvery((v) => ({ ...v, scope: val }))} className={`flex-1 rounded-full h-9 text-xs font-semibold border ${avery.scope === val ? "bg-teal text-white border-teal" : "bg-white"}`}>{lbl}</button>
                ))}
              </div>
            </div>
            {avery.scope === "class" && (
              <select data-testid="avery-class-select" className="w-full border rounded-md h-10 px-3" value={avery.classKey} onChange={(e) => setAvery((v) => ({ ...v, classKey: e.target.value }))}>
                <option value="">— choose a class/dance —</option>
                {averyClasses.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              </select>
            )}
            {avery.scope === "dancer" && (
              <div className="border rounded-lg divide-y max-h-44 overflow-y-auto">
                {averyDancers.length === 0 ? <p className="text-xs text-muted-foreground p-2">No dancers assigned from stock yet.</p> :
                  averyDancers.map((n) => (
                    <label key={n} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50">
                      <input type="checkbox" className="w-4 h-4 accent-teal" checked={avery.dancers.includes(n)} onChange={() => setAvery((v) => ({ ...v, dancers: v.dancers.includes(n) ? v.dancers.filter((x) => x !== n) : [...v.dancers, n] }))} />
                      <span>{n}</span>
                    </label>
                  ))}
              </div>
            )}
            <div>
              <Label>Start at label position (reuse a partial sheet)</Label>
              <div className="grid grid-cols-2 gap-1.5 mt-1.5 max-w-[220px]" data-testid="avery-start-grid">
                {Array.from({ length: ({ "5161": 20, "5160": 30, "5163": 10, "5162": 14 }[avery.template] || 20) }, (_, i) => i + 1).map((n) => (
                  <button key={n} type="button" data-testid={`avery-start-${n}`} onClick={() => setAvery((v) => ({ ...v, start: n }))} className={`h-7 rounded border text-xs ${avery.start === n ? "bg-teal text-white border-teal" : "bg-white text-muted-foreground"}`}>{n}</button>
                ))}
              </div>
            </div>
            <p className="text-xs" data-testid="avery-preview-count"><strong>{averyRows().length}</strong> label(s) will print, starting at position {avery.start}.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setAvery((v) => ({ ...v, open: false }))}>Cancel</Button>
            <Button data-testid="avery-download-button" className="rounded-full bg-teal hover:bg-teal-dark" onClick={downloadAvery}><Printer className="w-4 h-4 mr-1" />Download PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {toOrder.length > 0 && (
        <div data-testid="to-order-panel" className="bg-red-50 border border-red-100 rounded-2xl p-4">
          <p className="font-head font-bold flex items-center gap-2 text-red-700 mb-2"><ClipboardList className="w-4 h-4" />To Order — {toOrder.reduce((s, g) => s + g.count, 0)} item(s)</p>
          <div className="space-y-1">
            {toOrder.map((g, i) => (
              <div key={i} data-testid="to-order-row" className="text-sm flex flex-wrap items-center gap-x-2">
                <span className="font-semibold">{g.inventory_name}</span>
                <span className="rounded-full bg-white border border-red-200 text-red-700 text-xs px-2 py-0.5">{g.size || "size?"} × {g.count}</span>
                <span className="text-xs text-muted-foreground">{g.dancers.join(", ")}</span>
              </div>
            ))}
          </div>
          {isDirector && <p className="text-xs text-muted-foreground mt-2">Place these on the Costume Inventory page → Orders.</p>}
        </div>
      )}

      <AssignmentGroups assignments={assignments} items={items} canEdit={canEdit} saveFit={saveFit} reload={load} removeAssign={removeAssign} />

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-head">Assign a costume</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Costume from inventory</Label>
              <select data-testid="assign-inventory" className="w-full border rounded-md h-10 px-3" value={form.inventory_id} onChange={(e) => setForm({ ...form, inventory_id: e.target.value })}>
                <option value="">Select…</option>{inventory.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
              {inventory.length === 0 && <p className="text-xs text-muted-foreground">No costumes in inventory yet — add them on the Costume Inventory page.</p>}
            </div>
            <div className="space-y-1.5"><Label>Assign to</Label>
              <select data-testid="assign-target" className="w-full border rounded-md h-10 px-3" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })}>
                <option value="">Select a class or lead dance…</option>{targets.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter><Button data-testid="save-assign-button" onClick={assign} className="rounded-full bg-teal hover:bg-teal-dark">Assign</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
